import React, { KeyboardEvent, useCallback, useRef } from 'react';

import { IconsEnum } from '../../../../assets/icons/types';
import {
  ClassName,
  ErrorMessage,
  I18nText,
  IsDisabled
} from '../../../../types';

import {
  ProfileInputEditableFieldValidationProps,
  useProfileInputEditableField
} from './hooks/useProfileInputEditableField';

import { ProfileInputField } from '../ProfileInputField';

import { Form } from '../../../../helpers/Form';
import { PureIconButtonHelper } from '../../../../helpers/buttons/PureIconButtonHelper';

const enum KeyPress {
  ENTER = 'enter',
  ESCAPE = 'escape'
}

interface ProfileInputEditableFieldProps {
  fieldName: string;
  onSubmit: (updatedValue: string | number) => Promise<unknown>;
  onCancel?: () => void;
  onRemove?: () => void;
  onResetEdit?: () => void;
  defaultValue?: string;
  type?: string;
  step?: number | 'any';
  disabled?: IsDisabled;
  errorMessage?: ErrorMessage;
  prefix?: string;
  suffix?: string;
  i18nLabel?: I18nText;
  labelWrapperClassName?: ClassName;
  autoFocus?: boolean;
  className?: ClassName;
  inputClassName?: ClassName;
  showEditIcon?: boolean;
}

export function ProfileInputEditableField({
  fieldName,
  onSubmit,
  onCancel,
  onRemove,
  onResetEdit,
  defaultValue = '',
  type = 'text',
  step,
  disabled,
  errorMessage,
  prefix,
  suffix,
  i18nLabel,
  labelWrapperClassName,
  autoFocus,
  className,
  inputClassName,
  validationRules,
  validationSchema,
  showEditIcon = true
}: ProfileInputEditableFieldProps & ProfileInputEditableFieldValidationProps) {
  const {
    registerField,
    handleUpdate,
    getValueField,
    resetForm,
    validationErrors,
    trigger
  } = useProfileInputEditableField({
    fieldName,
    onSubmit,
    defaultValue,
    type,
    validationSchema,
    validationRules
  });

  const keyPress = useRef<KeyPress>(KeyPress.ENTER);

  const onBlur = useCallback<() => void>(async () => {
    const isValid = await trigger();

    if (
      keyPress.current === KeyPress.ESCAPE ||
      (errorMessage && getValueField === defaultValue)
    ) {
      keyPress.current = null;
      resetForm();
      onCancel?.();
    }

    if (getValueField !== defaultValue) {
      keyPress.current = null;
      handleUpdate();
    }

    isValid && onResetEdit?.();
  }, [
    trigger,
    errorMessage,
    getValueField,
    defaultValue,
    onResetEdit,
    resetForm,
    onCancel,
    handleUpdate
  ]);

  const onKeyDown = useCallback<(e: KeyboardEvent<HTMLInputElement>) => void>(
    (e) => {
      if (e.code === 'Enter' || e.code === 'NumpadEnter') {
        keyPress.current = KeyPress.ENTER;
        e.currentTarget.blur();
      }

      if (e.code === 'Escape') {
        keyPress.current = KeyPress.ESCAPE;
        e.currentTarget.blur();
      }
    },
    []
  );

  return (
    <Form onSubmit={handleUpdate} className={className}>
      <div className="flex">
        <ProfileInputField
          i18nLabel={i18nLabel}
          labelWrapperClassName={labelWrapperClassName}
          className="flex-grow flex flex-col sm:flex-row relative"
          inputClassName={
            inputClassName ||
            'number-appearance-none leading-none border-0 text-sm sm:text-base py-2 px-3 w-full rounded placeholder:italic bg-transparent dark:placeholder:text-gray-600 hover:bg-gray-200 dark:focus:bg-gray-800 dark:hover:bg-gray-800'
          }
          ref={registerField.ref}
          name={registerField.name}
          onChange={registerField.onChange}
          disabled={disabled}
          error={errorMessage || validationErrors}
          type={type}
          step={step}
          prefix={prefix}
          suffix={suffix}
          onBlur={onBlur}
          onKeyDown={onKeyDown}
          autoFocus={autoFocus}
          rightIcon={showEditIcon && !disabled && IconsEnum.PENCIL}
          autoComplete="off"
        />

        {onRemove && (
          <PureIconButtonHelper
            className="py-1.5 pl-1.5 pr-1.5 rounded-md inline-flex items-center whitespace-nowrap text-sm font-medium leading-5 focus:ring-base hover:text-gray-950 dark:hover:text-white hover:bg-gray-200 dark:hover:bg-gray-700 focus:ring-offset-0 flex-shrink self-center mr-1 text-gray-500"
            iconClassName="h-5 w-5"
            onClick={onRemove}
            icon={IconsEnum.TRASH_OUTLINE}
            disabled={disabled}
          />
        )}
      </div>
    </Form>
  );
}

export default ProfileInputEditableField;
